import React, { useState } from 'react';
import { MessageCircle, X, Send, User, Mail, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('welcome'); // welcome, form, success
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '', // Adding lastName to split full name or just separate field
    email: '',
    phone: ''
  });



  const formatPhoneNumber = (value) => {
    // Basic formatting for Spanish numbers if needed, or just raw
    return value;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const message = `Hola, mi nombre es ${formData.firstName} ${formData.lastName}. Mi email es ${formData.email} y mi teléfono es ${formData.phone}.`;
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/34637285137?text=${encodedMessage}`;

      // Redirigir a WhatsApp
      window.location.href = whatsappUrl;

      setStep('success');
      toast.success('¡Abriendo WhatsApp!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Hubo un error al iniciar el chat. Por favor intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group ${isOpen ? 'rotate-90 scale-0 opacity-0' : 'rotate-0 scale-100 opacity-100'
          }`}
        aria-label="Abrir chat"
      >
        <MessageCircle className="w-8 h-8 sm:w-9 sm:h-9" />
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-75"></span>
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-6 right-6 z-[60] w-[90vw] sm:w-[380px] bg-background border border-border rounded-2xl shadow-2xl transition-all duration-300 transform origin-bottom-right overflow-hidden flex flex-col ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
          }`}
        style={{ maxHeight: '80vh' }}
      >
        {/* Header */}
        <div className="bg-[#128C7E] p-4 flex items-center justify-between text-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-lg font-bold">E</span>
              </div>
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-[#128C7E]"></div>
            </div>
            <div>
              <h3 className="font-semibold text-sm">Clínica Eranza</h3>
              <p className="text-xs text-green-100">Responde en 5 minutos</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto grow bg-[#efeae2] bg-opacity-30">
          {step === 'welcome' && (
            <div className="space-y-4">
              <div className="bg-white p-3 rounded-lg rounded-tl-none shadow-sm max-w-[85%]">
                <p className="text-sm text-gray-800">
                  ¡Hola! 👋 Bienvenido a Clínica Eranza.
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg rounded-tl-none shadow-sm max-w-[85%]">
                <p className="text-sm text-gray-800">
                  Soy tu asistente virtual. Para conectarte con un especialista, por favor completa tus datos.
                </p>
              </div>
              <Button
                onClick={() => setStep('form')}
                className="w-full bg-[#128C7E] hover:bg-[#0e6f63] text-white rounded-full mt-4"
              >
                Iniciar Chat
              </Button>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="text-sm font-semibold mb-3 text-gray-700">Tus datos de contacto</h4>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500 ml-1">Nombre</label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <Input
                        name="firstName"
                        placeholder="Tu nombre"
                        className="pl-9 bg-gray-50 border-gray-200 focus:border-[#128C7E] focus:ring-[#128C7E]"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500 ml-1">Apellido</label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <Input
                        name="lastName"
                        placeholder="Tu apellido"
                        className="pl-9 bg-gray-50 border-gray-200 focus:border-[#128C7E] focus:ring-[#128C7E]"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500 ml-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <Input
                        name="email"
                        type="email"
                        placeholder="tucorreo@ejemplo.com"
                        className="pl-9 bg-gray-50 border-gray-200 focus:border-[#128C7E] focus:ring-[#128C7E]"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500 ml-1">Teléfono</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <Input
                        name="phone"
                        type="tel"
                        placeholder="+34 600 000 000"
                        className="pl-9 bg-gray-50 border-gray-200 focus:border-[#128C7E] focus:ring-[#128C7E]"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#128C7E] hover:bg-[#0e6f63] text-white rounded-full flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  'Conectando...'
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Iniciar Chat
                  </>
                )}
              </Button>
            </form>
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center justify-center h-full py-8 text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-2">
                <MessageCircle className="w-8 h-8 text-[#128C7E]" />
              </div>
              <h4 className="text-lg font-bold text-gray-800">¡Ya casi listo, {formData.firstName}!</h4>
              <p className="text-sm text-gray-600 max-w-[80%]">
                Haz clic en el botón de abajo para enviar tu mensaje por WhatsApp y comenzar el chat con tu asesora.
              </p>
              <Button
                onClick={() => {
                  const message = `Hola, mi nombre es ${formData.firstName} ${formData.lastName}. Mi email es ${formData.email} y mi teléfono es ${formData.phone}.`;
                  window.location.href = `https://wa.me/34637285137?text=${encodeURIComponent(message)}`;
                }}
                className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Abrir WhatsApp
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
